const API_BASE = process.env.REACT_APP_API_URL ?? "";

function getToken() {
    return localStorage.getItem("ks_token");
}

async function request(path, options = {}) {
    const token = getToken();
    const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
    };

    const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        const error = new Error(data.message || "Request failed");
        error.status = response.status;
        error.errors = data.errors || {};
        throw error;
    }

    return data;
}

const api = {
    get: (path) => request(path),
    // Default to {} — Bluehost ModSecurity rejects bodyless POST/PUT with 406
    post: (path, body = {}) => request(path, { method: "POST", body }),
    put: (path, body = {}) => request(path, { method: "PUT", body }),
    delete: (path) => request(path, { method: "DELETE" }),

    // Auth
    login: (credentials) => api.post("/api/login", credentials),
    register: (data) => api.post("/api/register", data),
    logout: () => api.post("/api/logout"),
    me: () => api.get("/api/me"),

    // Onboarding & clients
    submitOnboarding: (data) => api.post("/api/onboarding", data),
    pendingClients: () => api.get("/api/admin/clients/pending"),
    allClients: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== "" && v != null),
        ).toString();
        return api.get(`/api/admin/clients${qs ? `?${qs}` : ""}`);
    },
    clientCounts: () => api.get("/api/admin/clients/counts"),
    getClient: (userId) => api.get(`/api/admin/clients/${userId}`),
    approveClient: (userId) => api.post(`/api/admin/clients/${userId}/approve`),
    declineClient: (userId) => api.post(`/api/admin/clients/${userId}/decline`),
    archiveClient: (userId) => api.post(`/api/admin/clients/${userId}/archive`),
    deleteClient: (userId) => api.delete(`/api/admin/clients/${userId}`),
    createClient: (data) => api.post("/api/admin/clients", data),
    updateClient: (userId, data) =>
        api.put(`/api/admin/clients/${userId}`, data),

    // Projects
    projects: () => api.get("/api/projects"),
    project: (id) => api.get(`/api/projects/${id}`),
    createProject: (data) => api.post("/api/projects", data),
    updateProject: (id, data) => api.put(`/api/projects/${id}`, data),
    deleteProject: (id) => api.delete(`/api/projects/${id}`),
    clientProjects: (userId) =>
        api.get(`/api/admin/clients/${userId}/projects`),
    generateInvoice: (projectId, data) =>
        api.post(`/api/projects/${projectId}/generate-invoice`, data),

    // Project stages
    createStage: (projectId, data) =>
        api.post(`/api/projects/${projectId}/stages`, data),
    updateStage: (stageId, data) =>
        api.put(`/api/project-stages/${stageId}`, data),
    deleteStage: (stageId) => api.delete(`/api/project-stages/${stageId}`),

    // Time tracking
    startTimer: (projectId, data = {}) =>
        api.post(`/api/projects/${projectId}/time/start`, data),
    stopTimer: (projectId) => api.post(`/api/projects/${projectId}/time/stop`),
    addTimeEntry: (projectId, data) =>
        api.post(`/api/projects/${projectId}/time`, data),
    deleteTimeEntry: (entryId) => api.delete(`/api/time-entries/${entryId}`),

    // Project files (multipart — bypasses the JSON wrapper)
    uploadProjectFile: async (projectId, file, category = "file") => {
        const form = new FormData();
        form.append("file", file);
        form.append("category", category);
        const token = localStorage.getItem("ks_token");
        const response = await fetch(
            `${API_BASE}/api/projects/${projectId}/files`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: form,
            },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const error = new Error(data.message || "Upload failed");
            error.status = response.status;
            error.errors = data.errors || {};
            throw error;
        }
        return data;
    },
    downloadProjectFile: async (fileId, fileName) => {
        const token = localStorage.getItem("ks_token");
        const response = await fetch(
            `${API_BASE}/api/project-files/${fileId}/download`,
            {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            },
        );
        if (!response.ok) throw new Error("Download failed");
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    },
    deleteProjectFile: (fileId) => api.delete(`/api/project-files/${fileId}`),

    // QC / staging feedback (Ybug)
    updateYbugFeedback: (feedbackId, data) =>
        api.put(`/api/ybug-feedback/${feedbackId}`, data),

    // Admin stats
    adminStats: () => api.get("/api/admin/stats"),

    // Own account
    updateProfile: (data) => api.put("/api/me/profile", data),
    updatePassword: (data) => api.put("/api/me/password", data),
    resetClientPassword: (userId) =>
        api.post(`/api/admin/clients/${userId}/reset-password`),
    impersonateClient: (userId) =>
        api.post(`/api/admin/clients/${userId}/impersonate`),

    // Contact form submissions (admin)
    contactSubmissions: () => api.get("/api/admin/contact-submissions"),
    markSubmissionRead: (id) =>
        api.post(`/api/admin/contact-submissions/${id}/read`),
    deleteSubmission: (id) =>
        api.delete(`/api/admin/contact-submissions/${id}`),

    // Messages
    messages: (clientId) =>
        api.get(`/api/messages${clientId ? `?client_id=${clientId}` : ""}`),
    sendMessage: (data) => api.post("/api/messages", data),
    markMessagesRead: (clientId) =>
        api.post(
            "/api/messages/mark-read",
            clientId ? { client_id: clientId } : {},
        ),
    unreadMessages: () => api.get("/api/messages/unread-count"),
    messageThreads: () => api.get("/api/admin/message-threads"),

    // Services
    services: (category) =>
        api.get(`/api/services${category ? `?category=${category}` : ""}`),
    createService: (data) => api.post("/api/services", data),
    updateService: (id, data) => api.put(`/api/services/${id}`, data),
    deleteService: (id) => api.delete(`/api/services/${id}`),

    // Quotes
    quotes: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries({ page: 1, ...params }).filter(
                ([, v]) => v !== "" && v != null,
            ),
        ).toString();
        return api.get(`/api/quotes?${qs}`);
    },
    quote: (id) => api.get(`/api/quotes/${id}`),
    createQuote: (data) => api.post("/api/quotes", data),
    updateQuote: (id, data) => api.put(`/api/quotes/${id}`, data),
    deleteQuote: (id) => api.delete(`/api/quotes/${id}`),
    convertToInvoice: (id, data) =>
        api.post(`/api/quotes/${id}/convert-to-invoice`, data),
    convertToProject: (id) => api.post(`/api/quotes/${id}/convert-to-project`),
    acceptQuote: (id) => api.post(`/api/quotes/${id}/accept`),
    declineQuote: (id) => api.post(`/api/quotes/${id}/decline`),
    sendQuoteEmail: (id) => api.post(`/api/quotes/${id}/send-email`),

    // Invoices
    invoices: (params = {}) => {
        const qs = new URLSearchParams(
            Object.entries({ page: 1, ...params }).filter(
                ([, v]) => v !== "" && v != null,
            ),
        ).toString();
        return api.get(`/api/invoices?${qs}`);
    },
    invoice: (id) => api.get(`/api/invoices/${id}`),
    createInvoice: (data) => api.post("/api/invoices", data),
    updateInvoice: (id, data) => api.put(`/api/invoices/${id}`, data),
    deleteInvoice: (id) => api.delete(`/api/invoices/${id}`),
    sendInvoiceEmail: (id) => api.post(`/api/invoices/${id}/send-email`),
};

export default api;
